import {
  BffResponse,
  CarData,
  ModelFilterTechnicalDataGroup,
  TRIM_FILTER,
} from '@volkswagen-onehub/car-technical-details-bff';
import {
  Breakpoints,
  ContentLayerV2Content,
  styled,
  Text,
  TextAlignment,
  TextTag,
  TokenTextAppearance,
} from '@volkswagen-onehub/components-core';
import {LayerManagerV27} from '@volkswagen-onehub/layer-manager';
import {PageInfoServiceV1} from '@volkswagen-onehub/page-info-service';
import React from 'react';
import {ViewMode} from '..';
import {useFeatureServices} from '../use-feature-services';
import {inspectDataHolder} from '../utils/inspector/inspector';
import {I18nContext, translate, TranslateFunction} from '../utils/translate';
import {useInspectFa} from '../utils/useInspectFa';
import {useTrackingService} from '../utils/useTrackingService';
import {createCloseTopLayerFunction} from './create-close-top-layer-fn';
import {NoDataPage} from './noData';
import {TechnicalDataSection} from './section';
import {TrimFilter} from './TrimFilter';

export const StyledMain = styled.main<{isEmbedded?: boolean}>`
  // NOTE: content layer top margin - top/bottom padding - sticky heading
  min-height: calc(
    100vh - ${(props) => props.theme.size.dynamic0350} - 2 *
      (${(props) => props.theme.size.dynamic0300}) - 52px
  );
  max-width: ${(props) =>
    props.isEmbedded ? props.theme.size.grid022 : props.theme.size.grid020};
  display: flex;
  flex-direction: column;
  justify-content: center;
  align-items: center;
  hyphens: auto;
  margin: 0 auto;
  padding: ${(props) => (props.isEmbedded ? props.theme.size.dynamic0300 : '')}
    0;

  & .primary-headline {
    padding-bottom: ${(props) => props.theme.size.dynamic0150};
  }

  // NOTE:fixes cut-off focus outline on focusable table
  & table[tabindex='0'] {
    padding-left: 1px;
    padding-right: 1px;
  }

  @media (min-width: ${Breakpoints.b960}px) {
    ${(props) =>
      !props.isEmbedded ? `max-width: ${props.theme.size.grid016};` : ''}
  }
`;

interface AppProps {
  data: BffResponse;
  viewMode: ViewMode;
  layerManager: LayerManagerV27;
  withoutHeader?: boolean;
  bffUrl: string;
}

export type PromiseStatus =
  | ErrorPromiseState
  | SuccessPromiseState
  | IdlePromiseState;
interface SuccessPromiseState {
  status: 'success';
  data: BffResponse;
}
interface ErrorPromiseState {
  status: 'error';
  errorMsg: string;
}
interface IdlePromiseState {
  status: 'idle';
}

export function App(props: AppProps): JSX.Element {
  // const {'s2:logger': logger = console} = useFeatureServices();
  const {data, viewMode, layerManager} = props;
  const [bffResponse, setBffResponse] = React.useState(data);
  const trackingService = useTrackingService();
  const {'page-info-service': pageInfoService} = useFeatureServices();
  useInspectFa();

  inspectDataHolder.modelsFromResponse = bffResponse.models;

  // create translate function (and update if data has been changed)
  const translateFn = React.useCallback(() => {
    return translate(bffResponse.translations || {});
  }, [bffResponse])();

  // track on open/close layer
  React.useEffect(() => {
    if (viewMode === 'layer') {
      trackingService.trackLayerLoad();
    }

    return () => {
      if (viewMode === 'layer') {
        trackingService.trackLayerClose();
      }
    };
  }, [trackingService, viewMode]);

  // update seo meta data on page info service if content is ready
  React.useEffect(() => {
    if (bffResponse?.carData && viewMode === 'layer') {
      const modelPlaceholders = createModelPlaceHolders(bffResponse.carData);
      updatePageInfo(pageInfoService, translateFn, modelPlaceholders);
    }
  }, [bffResponse, pageInfoService, translateFn, viewMode]);

  // function that renders the app
  const render = () => {
    const carData = bffResponse.carData;
    if (!carData || !bffResponse?.carData?.technicalDataGroups?.length) {
      return (
        <I18nContext.Provider value={translateFn}>
          <NoDataPage />
        </I18nContext.Provider>
      );
    }

    const modelPlaceholders = createModelPlaceHolders(carData);
    const name =
      translateFn('header.headline', modelPlaceholders) || carData.carlineName;
    let nameExtension = undefined;
    if (!bffResponse.acs?.header?.skipHeadlineExtension) {
      nameExtension =
        translateFn('header.headlineExtension', modelPlaceholders) ||
        carData.trimNames;
    }
    return (
      <I18nContext.Provider value={translateFn}>
        <Text
          className="primary-headline"
          tag={TextTag.h2}
          appearance={TokenTextAppearance.headline400}
          textAlign={TextAlignment.center}
        >
          <strong>{name}</strong> {nameExtension}
        </Text>

        {carData.filters?.[TRIM_FILTER] ? (
          <TrimFilter
            filter={carData.filters[TRIM_FILTER]}
            bffUrl={props.bffUrl}
            setBffResponse={setBffResponse}
          />
        ) : null}

        {bffResponse.carData?.technicalDataGroups.map(
          (g: ModelFilterTechnicalDataGroup, index: number) => (
            <TechnicalDataSection
              key={`${g.name || g.key}+${
                carData.filters?.[TRIM_FILTER].defaultOption ?? ''
              }`}
              group={g}
              open={index === 0}
              index={index}
              count={bffResponse.carData?.technicalDataGroups.length || 0}
            />
          )
        )}
      </I18nContext.Provider>
    );
  };

  // render the app within the correct context (layer or page embedded)
  if (viewMode === 'layer') {
    const closeLayer = createCloseTopLayerFunction(
      layerManager,
      pageInfoService
    );
    if (props.withoutHeader) {
      const modelPlaceholders = bffResponse.carData
        ? createModelPlaceHolders(bffResponse.carData)
        : {};
      return (
        <ContentLayerV2Content
          closeButtonLabel={translateFn('layer.closebutton.aria.label')}
          contentHeadline={translateFn('layer.headline', modelPlaceholders)}
          onClose={closeLayer}
        >
          <StyledMain>{render()}</StyledMain>
        </ContentLayerV2Content>
      );
    } else {
      return <StyledMain>{render()}</StyledMain>;
    }
  } else {
    return <StyledMain isEmbedded>{render()}</StyledMain>;
  }
}

function createModelPlaceHolders(carData: CarData): Record<string, string> {
  return {
    carlineName: carData.carlineName,
    trimNames: carData.trimNames || '',
    salesGroupNames: carData.salesGroupNames || '',
  };
}

function updatePageInfo(
  pageInfoService: PageInfoServiceV1,
  translateFn: TranslateFunction,
  modelPlaceholders: Record<string, string>
) {
  const seoTitle = translateFn('metadata.title', modelPlaceholders);
  const seoDescription = translateFn('metadata.description', modelPlaceholders);

  pageInfoService.setPageInfo({
    seo: {
      pageTitle: seoTitle,
      description: seoDescription,
      og: {
        ogTitle: seoTitle,
        ogDescription: seoDescription,
      },
      twitter: {
        twitterTitle: seoTitle,
        twitterDescription: seoDescription,
      },
    },
  });
}
